import React, { useCallback, useMemo } from "react";
import { Lock } from "@mui/icons-material";
import { Box } from "@mui/material";

import { makeFondVectorTileURL } from "fond/api";
import { apiSlice } from "fond/api/apiSlice";
import { useDeleteLayerMutation } from "fond/api/layersSlice";
import { useFeatureFlag } from "fond/featureFlags";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useGetImportLayers } from "fond/hooks/useGetImportLayers";
import { refreshTileSource } from "fond/map/redux";
import VectorLayer from "fond/map/VectorLayer";
import LayerDraw from "fond/project/panels/InputPanel/LayerDraw";
import LayerPanel from "fond/project/panels/InputPanel/LayerPanel";
import LayerUploadWrapper from "fond/project/panels/InputPanel/LayerUploadWrapper";
import MultiLayerUploadForm from "fond/project/panels/InputPanel/MultiLayerUploadForm";
import SolveOutputControlPanel from "fond/project/panels/InputPanel/SolveOutputControlPanel";
import MuPowerArchitecturePanel from "fond/project/panels/MuPower/architecture/MuPowerArchitecturePanel";
import { usePowerArchitecture } from "fond/project/panels/MuPower/architecture/usePowerArchitecture";
import {
  MuPowerEditableLayerIds,
  MuPowerInputLayerIds,
  MuPowerOutputLayerIds,
  MuPowerSolverLayerId,
  MuPowerSolverLayerIds,
  Project,
  WORKFLOW_KEYS,
} from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { isImportInProgress } from "fond/utils/layers";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AccordionStepper, AccordionStepperPane, NonIdealState, StackedNavigation } from "fond/widgets";

import { muPowerInputLayers } from "./InputPanel/layers";
import MuPowerBoundaryImport from "./InputPanel/MuPowerBoundaryImport";
import MuPowerBoundaryPanel from "./InputPanel/MuPowerBoundaryPanel";
import ConstructionPrintExport from "./ConstructionPrintExport";
import DataPrepPanel from "./DataPrepPanel";
import DesignArtifactsPanel from "./DesignArtifactsPanel";
import { InputPanel } from "./InputPanel";
import MuPowerBlockersDraw from "./MuPowerBlockersDraw";
import MuPowerDraw from "./MuPowerDraw";
import MuPowerFootprintDraw from "./MuPowerFootprintDraw";
import MuPowerLoopConnectionsDraw from "./MuPowerLoopConnectionsDraw";
import MuPowerPhaseOverridesDraw from "./MuPowerPhaseOverridesDraw";
import MuPowerPhaseOverridesPanel from "./MuPowerPhaseOverridesPanel";
import MuPowerSourceHubsDraw from "./MuPowerSourceHubsDraw";
import MuPowerThreePhasePathDraw from "./MuPowerThreePhasePathDraw";
import MuPowerTransformersDraw from "./MuPowerTransformersDraw";
import PowerSolverPanel from "./PowerSolverPanel";

interface PanelProps {
  project: Project;
  readOnly: boolean;
}

const Panel: React.FC<PanelProps> = ({ project, readOnly }: PanelProps) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const { value: areaSelectDatasetTilesEnabled } = useFeatureFlag("area_select_dataset_tiles");
  const inputLayers = useGetImportLayers(versionId, Object.values(MuPowerInputLayerIds));
  const solverLayers = useGetImportLayers(versionId, Object.values(MuPowerSolverLayerIds));

  const { powerArchitecture } = usePowerArchitecture(versionId);
  const hasArchitecture = !!powerArchitecture;

  const hasInProgressLayers = useMemo(() => {
    return Array.from(inputLayers.values()).some((layer) => layer.status && isImportInProgress(layer.status));
  }, [inputLayers]);

  const allRequiredLayersUploaded = useMemo(() => {
    return muPowerInputLayers.filter(({ isRequired }) => isRequired).every(({ key }) => !!inputLayers.get(key)?.layer);
  }, [inputLayers]);

  const { workflowState: dataPrepWorkflow } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.MU_POWER.DATA_PREP });
  const isDataPrepInProgress = dataPrepWorkflow === "STARTING" || dataPrepWorkflow === "IN_PROGRESS";

  const { workflowState: powerSolverWorkflow } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.MU_POWER.POWER_SOLVER });
  const isPowerSolverInProgress = powerSolverWorkflow === "STARTING" || powerSolverWorkflow === "IN_PROGRESS";

  const dataPrepComplete = dataPrepWorkflow === "COMPLETE";

  const [deleteLayer] = useDeleteLayerMutation();
  const deleteLayersByKey = useCallback(
    async (layerKeys: MuPowerSolverLayerId[]) => {
      const ids = layerKeys.map((key) => solverLayers.get(key)?.layer?.ID).filter((id): id is string => !!id);
      if (ids.length === 0) return;
      await Promise.all(ids.map((id) => deleteLayer(id).unwrap()));
      dispatch(refreshTileSource("project-layers"));
      dispatch(
        apiSlice.util.invalidateTags([
          { type: "FeatureTotals", id: versionId },
          { type: "Layers", id: versionId },
          { type: "VersionRootConfiguration", id: versionId },
          { type: "ImportStatus", id: versionId },
        ])
      );
    },
    [deleteLayer, solverLayers, dispatch, versionId]
  );

  const muPowerStackedNavigationPanels = useMemo<Array<{ id: string; name: string; component: React.ReactNode; useDefaultHeader?: boolean }>>(() => {
    const layerPanels = muPowerInputLayers.flatMap(
      ({ key, displayName, navigationKey, geometryType, drawingConfig, filterFeatures, combineToMultiPolygon }) => {
        const uploadScreen = {
          id: `${navigationKey}-upload`,
          name: "Upload",
          component: (
            <LayerUploadWrapper
              layerId={key}
              displayName={displayName}
              title={`Upload ${displayName.toLowerCase()}`}
              geometryType={geometryType}
              hasExistingData={!!inputLayers.get(key)?.layer}
            />
          ),
          useDefaultHeader: false,
        };

        const drawScreen = {
          id: `${navigationKey}-draw`,
          name: `Draw ${displayName.toLowerCase()}`,
          component: (
            <LayerDraw
              versionId={versionId}
              layerId={key}
              displayName={displayName}
              drawingConfig={drawingConfig}
              filterFeatures={filterFeatures}
              combineToMultiPolygon={combineToMultiPolygon}
              mapContent={
                key === MuPowerInputLayerIds.boundary ? (
                  <>
                    <VectorLayer
                      id="parcel-tiles"
                      type="fill"
                      source={{
                        type: "vector",
                        tiles: [makeFondVectorTileURL("parcels")],
                        minzoom: 10,
                        maxzoom: 24,
                      }}
                      sourceLayer="default"
                      paint={{
                        "fill-opacity": 0.08,
                        "fill-color": "purple",
                        "fill-outline-color": "gray",
                      }}
                    />
                    <VectorLayer
                      id="address-tiles"
                      type="circle"
                      minzoom={13}
                      source={{
                        type: "vector",
                        tiles: [makeFondVectorTileURL("addresses")],
                        minzoom: areaSelectDatasetTilesEnabled ? 0 : 13,
                        maxzoom: areaSelectDatasetTilesEnabled ? 11 : 24,
                      }}
                      sourceLayer="default"
                      paint={{
                        "circle-opacity": 0.5,
                        "circle-radius": 1.5,
                        "circle-color": "#000000",
                      }}
                    />
                  </>
                ) : undefined
              }
            />
          ),
          useDefaultHeader: false,
        };

        if (key === MuPowerInputLayerIds.boundary) {
          return [
            {
              id: key,
              name: displayName,
              component: <MuPowerBoundaryPanel hasBoundary={!!inputLayers.get(key)?.layer} />,
              useDefaultHeader: false,
            },
            drawScreen,
            uploadScreen,
            {
              id: `${navigationKey}-import`,
              name: "Import",
              component: <MuPowerBoundaryImport />,
              useDefaultHeader: false,
            },
          ];
        }

        return [
          {
            id: key,
            name: displayName,
            component: <LayerPanel title={displayName} layerKey={navigationKey} actions={["draw", "upload"]} />,
            useDefaultHeader: false,
          },
          drawScreen,
          uploadScreen,
        ];
      }
    );

    const sourceHubsKey = MuPowerSolverLayerIds.sourceHubs;
    const sourceHubsHasData = !!solverLayers.get(sourceHubsKey)?.layer;
    const transformerHubsKey = MuPowerSolverLayerIds.transformerHubs;
    const transformerHubsHasData = !!solverLayers.get(transformerHubsKey)?.layer;

    return [
      ...layerPanels,
      {
        id: "modification-muPowerBaseData",
        name: "Base Data",
        component: (
          <LayerPanel
            title="Base Data"
            layerKey="mu-base-data"
            actions={["draw", "upload"]}
            drawTitle="Edit Base Data"
            drawSubtitle="Edit or draw base data layers on the map"
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-base-data-draw",
        name: "Edit Base Data",
        component: <MuPowerDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-base-data-upload",
        name: "Upload Base Data",
        component: (
          <MultiLayerUploadForm
            title="Upload Base Data"
            sections={[
              { layerId: MuPowerEditableLayerIds.ugPath, displayName: "UG Path", geometryType: "LineString" },
              { layerId: MuPowerEditableLayerIds.ugPoints, displayName: "UG Points", geometryType: "Point" },
              { layerId: MuPowerEditableLayerIds.leadins, displayName: "Lead-ins", geometryType: "LineString" },
            ]}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: sourceHubsKey,
        name: "Source Hubs",
        component: (
          <LayerPanel
            title="Source Hubs"
            layerKey="mu-source-hubs"
            actions={["draw", "upload", "delete"]}
            drawTitle="Edit Source Hubs"
            drawSubtitle="Edit or draw source hubs on the map"
            canDelete={sourceHubsHasData}
            onDelete={() => deleteLayersByKey([sourceHubsKey])}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-source-hubs-upload",
        name: "Upload",
        component: (
          <LayerUploadWrapper
            layerId={sourceHubsKey}
            displayName="Source Hubs"
            title="Upload source hubs"
            geometryType="Point"
            hasExistingData={sourceHubsHasData}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-source-hubs-draw",
        name: "Source Hubs",
        component: <MuPowerSourceHubsDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-blockers",
        name: "Blockers",
        component: (
          <LayerPanel
            title="Blockers"
            layerKey="mu-blockers"
            actions={["draw", "upload", "delete"]}
            drawTitle="Edit Blockers"
            drawSubtitle="Edit or draw blockers on the map"
            canDelete={
              !!solverLayers.get(MuPowerSolverLayerIds.primaryBlockers)?.layer || !!solverLayers.get(MuPowerSolverLayerIds.secondaryBlockers)?.layer
            }
            onDelete={() => deleteLayersByKey([MuPowerSolverLayerIds.primaryBlockers, MuPowerSolverLayerIds.secondaryBlockers])}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-blockers-draw",
        name: "Edit Blockers",
        component: <MuPowerBlockersDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-blockers-upload",
        name: "Upload Blockers",
        component: (
          <MultiLayerUploadForm
            title="Upload Blockers"
            sections={[
              { layerId: MuPowerSolverLayerIds.primaryBlockers, displayName: "Primary Blockers", geometryType: "LineString" },
              { layerId: MuPowerSolverLayerIds.secondaryBlockers, displayName: "Secondary Blockers", geometryType: "LineString" },
            ]}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-footprint",
        name: "Footprint",
        component: (
          <SolveOutputControlPanel
            layerPairs={[
              { controlKey: MuPowerSolverLayerIds.primaryFootprint, outputKey: MuPowerOutputLayerIds.primaryWires },
              { controlKey: MuPowerSolverLayerIds.secondaryFootprint, outputKey: MuPowerOutputLayerIds.secondaryWires },
            ]}
            displayName="Footprint"
            editPanelTarget="mu-footprint-draw"
            uploadPanelTarget="mu-footprint-upload"
            canDelete={
              !!solverLayers.get(MuPowerSolverLayerIds.primaryFootprint)?.layer || !!solverLayers.get(MuPowerSolverLayerIds.secondaryFootprint)?.layer
            }
            onDelete={() => deleteLayersByKey([MuPowerSolverLayerIds.primaryFootprint, MuPowerSolverLayerIds.secondaryFootprint])}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-footprint-draw",
        name: "Edit Footprint",
        component: <MuPowerFootprintDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-footprint-upload",
        name: "Upload Footprint",
        component: (
          <MultiLayerUploadForm
            title="Upload Footprint"
            sections={[
              { layerId: MuPowerSolverLayerIds.primaryFootprint, displayName: "Primary Footprint", geometryType: "LineString" },
              { layerId: MuPowerSolverLayerIds.secondaryFootprint, displayName: "Secondary Footprint", geometryType: "LineString" },
            ]}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-transformers",
        name: "Transformers",
        component: (
          <SolveOutputControlPanel
            layerPairs={[{ controlKey: transformerHubsKey, outputKey: MuPowerOutputLayerIds.transformers }]}
            displayName="Transformers"
            editPanelTarget="mu-transformers-draw"
            uploadPanelTarget="mu-transformers-upload"
            canDelete={transformerHubsHasData}
            onDelete={() => deleteLayersByKey([transformerHubsKey])}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-transformers-draw",
        name: "Edit Transformers",
        component: <MuPowerTransformersDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-transformers-upload",
        name: "Upload",
        component: (
          <LayerUploadWrapper
            layerId={transformerHubsKey}
            displayName="Transformers"
            title="Upload transformers"
            geometryType="Point"
            hasExistingData={transformerHubsHasData}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-three-phase-path",
        name: "3-phase path",
        component: (
          <LayerPanel
            title="3-phase path"
            layerKey={MuPowerSolverLayerIds.primaryWireOverrides}
            actions={["draw", "upload", "delete"]}
            canDelete={!!solverLayers.get(MuPowerSolverLayerIds.primaryWireOverrides)?.layer}
            onDelete={() => deleteLayersByKey([MuPowerSolverLayerIds.primaryWireOverrides])}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: `${MuPowerSolverLayerIds.primaryWireOverrides}-draw`,
        name: "Edit 3-phase path",
        component: <MuPowerThreePhasePathDraw />,
        useDefaultHeader: false,
      },
      {
        id: "mu-loop-connections",
        name: "Loop connections",
        component: (
          <LayerPanel
            title="Loop connections"
            layerKey={MuPowerSolverLayerIds.loopConnections}
            actions={["draw", "upload", "delete"]}
            canDelete={!!solverLayers.get(MuPowerSolverLayerIds.loopConnections)?.layer}
            onDelete={() => deleteLayersByKey([MuPowerSolverLayerIds.loopConnections])}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: `${MuPowerSolverLayerIds.loopConnections}-draw`,
        name: "Edit loop connections",
        component: <MuPowerLoopConnectionsDraw />,
        useDefaultHeader: false,
      },
      {
        id: `${MuPowerSolverLayerIds.loopConnections}-upload`,
        name: "Upload",
        component: (
          <LayerUploadWrapper
            layerId={MuPowerSolverLayerIds.loopConnections}
            displayName="Loop connections"
            title="Upload loop connections"
            geometryType="LineString"
            hasExistingData={!!solverLayers.get(MuPowerSolverLayerIds.loopConnections)?.layer}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-phase-overrides",
        name: "Phase overrides",
        component: (
          <MuPowerPhaseOverridesPanel
            versionId={versionId}
            hasExistingData={!!solverLayers.get(MuPowerSolverLayerIds.phaseOverrides)?.layer}
            onDelete={() => deleteLayersByKey([MuPowerSolverLayerIds.phaseOverrides])}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: `${MuPowerSolverLayerIds.phaseOverrides}-draw`,
        name: "Edit phase overrides",
        component: <MuPowerPhaseOverridesDraw />,
        useDefaultHeader: false,
      },
      {
        id: `${MuPowerSolverLayerIds.phaseOverrides}-upload`,
        name: "Upload",
        component: (
          <LayerUploadWrapper
            layerId={MuPowerSolverLayerIds.phaseOverrides}
            displayName="Phase overrides"
            title="Upload phase overrides"
            geometryType="Point"
            hasExistingData={!!solverLayers.get(MuPowerSolverLayerIds.phaseOverrides)?.layer}
          />
        ),
        useDefaultHeader: false,
      },
      {
        id: "mu-construction-prints-export",
        name: "Export",
        component: <ConstructionPrintExport />,
        useDefaultHeader: false,
      },
    ];
  }, [solverLayers, versionId, inputLayers, areaSelectDatasetTilesEnabled, deleteLayersByKey]);

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Architecture",
        id: "architecture",
        content: <MuPowerArchitecturePanel systemOfMeasurement={project.SystemOfMeasurement} isReadOnly={readOnly} />,
        isComplete: hasArchitecture,
      },
      {
        header: "Input data",
        id: "inputData",
        content: <InputPanel isReadOnly={readOnly} inputLayers={inputLayers} />,
        isComplete: allRequiredLayersUploaded && !hasInProgressLayers,
        isLoading: hasInProgressLayers,
      },
      {
        header: "Data preparation",
        id: "dataPrep",
        content: <DataPrepPanel isReadOnly={readOnly} canRun={allRequiredLayersUploaded} />,
        isComplete: dataPrepComplete,
        isLoading: isDataPrepInProgress,
      },
      {
        header: "Design",
        id: "powerSolver",
        content: <PowerSolverPanel project={project} isReadOnly={readOnly} canRun={dataPrepComplete} solverLayers={solverLayers} />,
        isComplete: powerSolverWorkflow === "COMPLETE",
        isLoading: isPowerSolverInProgress,
      },
      {
        header: "Artefacts",
        id: "designArtifacts",
        content: <DesignArtifactsPanel project={project} />,
        isComplete: false,
      },
    ],
    [
      allRequiredLayersUploaded,
      dataPrepComplete,
      hasArchitecture,
      hasInProgressLayers,
      inputLayers,
      isDataPrepInProgress,
      isPowerSolverInProgress,
      powerSolverWorkflow,
      project,
      readOnly,
      solverLayers,
    ]
  );

  if (!project) return null;

  return (
    <Box data-testid="mu-power-design-panel" sx={{ height: "100%" }}>
      {!permissionCheck(project.Permission?.Level, Actions.PROJECT_EDIT) ? (
        <NonIdealState size="small" icon={<Lock />} title="View only access" description="You only have view access to this project" />
      ) : (
        <StackedNavigation
          rootComponent={<AccordionStepper panes={accordionPanes} id={`${project.ID}-mu-power`} autoMove={false} />}
          screens={muPowerStackedNavigationPanels}
        />
      )}
    </Box>
  );
};

export default Panel;
